import React, { useEffect, useState } from "react";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";

interface Order {
 _id: string;
 orderNumber: string;
 total: number;
 platformFee: number;
 currency: string;
 status: string;
 createdAt: string;
}

interface Payout {
 _id: string;
 storeId: {
  _id: string;
  name: string;
 };
 amount: number;
 currency: string;
 status: "pending" | "processing" | "completed" | "failed" | "cancelled";
 payoutMethod: string;
 payoutDetails?: any;
 periodStart: string;
 periodEnd: string;
 orders: Order[];
 platformFeeTotal: number;
 transactionId?: string;
 processedAt?: string;
 failureReason?: string;
 metadata?: any;
 createdAt: string;
 updatedAt: string;
}

const PayoutDetail: React.FC = () => {
 const { isAuthenticated } = usePlatformAuth();
 const navigate = useNavigate();
 const { payoutId } = useParams<{ payoutId: string }>();
 const [payout, setPayout] = useState<Payout | null>(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  if (payoutId) {
   fetchPayout();
  }
 }, [isAuthenticated, navigate, payoutId]);

 const fetchPayout = async () => {
  try {
   setLoading(true);
   const response = await platformApiService.getPayout(payoutId!);
   setPayout(response.data.payout);
  } catch (error) {
   console.error("Error fetching payout:", error);
  } finally {
   setLoading(false);
  }
 };

 const handleProcessPayout = async () => {
  if (!window.confirm("Are you sure you want to process this payout?")) {
   return;
  }

  try {
   await platformApiService.processPayout(payoutId!);
   fetchPayout();
  } catch (error) {
   console.error("Error processing payout:", error);
   alert("Failed to process payout");
  }
 };

 const handleCancelPayout = async () => {
  if (!window.confirm("Are you sure you want to cancel this payout?")) {
   return;
  }

  try {
   await platformApiService.cancelPayout(payoutId!);
   fetchPayout();
  } catch (error) {
   console.error("Error cancelling payout:", error);
   alert("Failed to cancel payout");
  }
 };

 const getStatusColor = (status: string) => {
  switch (status) {
   case "completed":
    return "bg-green-100 text-green-800";
   case "pending":
    return "bg-yellow-100 text-yellow-800";
   case "processing":
    return "bg-blue-100 text-blue-800";
   case "failed":
    return "bg-red-100 text-red-800";
   case "cancelled":
    return "bg-gray-100 text-gray-800";
   default:
    return "bg-gray-100 text-gray-800";
  }
 };

 if (loading) {
  return <div className="p-8">Loading payout details...</div>;
 }

 if (!payout) {
  return <div className="p-8">Payout not found</div>;
 }

 return (
  <div className="p-8">
   <div className="mb-6">
    <Link
     to="/platform/payouts"
     className="text-indigo-600 hover:text-indigo-900 mb-4 inline-block"
    >
     ← Back to Payouts
    </Link>
    <h1 className="text-3xl font-bold">Payout Details</h1>
   </div>

   <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    {/* Main Details */}
    <div className="lg:col-span-2 space-y-6">
     {/* Payout Information */}
     <div className="bg-white shadow p-6">
      <h2 className="text-xl font-bold mb-4">Payout Information</h2>
      <div className="grid grid-cols-2 gap-4">
       <div>
        <label className="text-sm font-medium text-gray-500">Store</label>
        <div className="mt-1">
         <Link
          to={`/platform/stores/${payout.storeId._id}`}
          className="text-indigo-600 hover:text-indigo-900 font-medium"
         >
          {payout.storeId.name}
         </Link>
        </div>
       </div>
       <div>
        <label className="text-sm font-medium text-gray-500">Amount</label>
        <div className="mt-1 text-lg font-semibold">
         {payout.currency} {payout.amount.toFixed(2)}
        </div>
       </div>
       <div>
        <label className="text-sm font-medium text-gray-500">Status</label>
        <div className="mt-1">
         <span
          className={`px-3 py-1 text-sm font-semibold ${getStatusColor(
           payout.status
          )}`}
         >
          {payout.status}
         </span>
        </div>
       </div>
       <div>
        <label className="text-sm font-medium text-gray-500">Payout Method</label>
        <div className="mt-1">{payout.payoutMethod}</div>
       </div>
       <div>
        <label className="text-sm font-medium text-gray-500">Period Start</label>
        <div className="mt-1">
         {new Date(payout.periodStart).toLocaleString()}
        </div>
       </div>
       <div>
        <label className="text-sm font-medium text-gray-500">Period End</label>
        <div className="mt-1">
         {new Date(payout.periodEnd).toLocaleString()}
        </div>
       </div>
       <div>
        <label className="text-sm font-medium text-gray-500">Platform Fee Total</label>
        <div className="mt-1">
         {payout.currency} {payout.platformFeeTotal.toFixed(2)}
        </div>
       </div>
       <div>
        <label className="text-sm font-medium text-gray-500">Orders Count</label>
        <div className="mt-1">{payout.orders.length}</div>
       </div>
       {payout.transactionId && (
        <div>
         <label className="text-sm font-medium text-gray-500">Transaction ID</label>
         <div className="mt-1 font-mono text-sm">{payout.transactionId}</div>
        </div>
       )}
       {payout.processedAt && (
        <div>
         <label className="text-sm font-medium text-gray-500">Processed At</label>
         <div className="mt-1">
          {new Date(payout.processedAt).toLocaleString()}
         </div>
        </div>
       )}
       {payout.failureReason && (
        <div className="col-span-2">
         <label className="text-sm font-medium text-gray-500">Failure Reason</label>
         <div className="mt-1 text-red-600">{payout.failureReason}</div>
        </div>
       )}
      </div>
     </div>

     {/* Orders Included */}
     <div className="bg-white shadow p-6">
      <h2 className="text-xl font-bold mb-4">Orders Included ({payout.orders.length})</h2>
      <div className="overflow-x-auto">
       <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
         <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           Order Number
          </th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           Total
          </th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           Platform Fee
          </th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           Status
          </th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           Date
          </th>
         </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
         {payout.orders.map((order) => (
          <tr key={order._id}>
           <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
            {order.orderNumber}
           </td>
           <td className="px-4 py-3 whitespace-nowrap text-sm">
            {order.currency} {order.total.toFixed(2)}
           </td>
           <td className="px-4 py-3 whitespace-nowrap text-sm">
            {order.currency} {(order.platformFee || 0).toFixed(2)}
           </td>
           <td className="px-4 py-3 whitespace-nowrap text-sm">
            <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800">
             {order.status}
            </span>
           </td>
           <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
            {new Date(order.createdAt).toLocaleDateString()}
           </td>
          </tr>
         ))}
        </tbody>
       </table>
      </div>
     </div>
    </div>

    {/* Actions Sidebar */}
    <div className="space-y-6">
     <div className="bg-white shadow p-6">
      <h2 className="text-xl font-bold mb-4">Actions</h2>
      <div className="space-y-3">
       {payout.status === "pending" && (
        <>
         <button
          onClick={handleProcessPayout}
          className="w-full px-4 py-2 bg-green-600 text-white hover:bg-green-700"
         >
          Process Payout
         </button>
         <button
          onClick={handleCancelPayout}
          className="w-full px-4 py-2 bg-red-600 text-white hover:bg-red-700"
         >
          Cancel Payout
         </button>
        </>
       )}
       {payout.status === "processing" && (
        <div className="text-sm text-gray-500">
         Payout is being processed. Please wait or contact support if it takes too long.
        </div>
       )}
       {payout.status === "completed" && (
        <div className="text-sm text-green-600">
         ✓ Payout completed successfully
        </div>
       )}
       {payout.status === "failed" && (
        <div className="text-sm text-red-600">
         Payout failed. Check failure reason above.
        </div>
       )}
      </div>
     </div>

     {/* Payout Details */}
     {payout.payoutDetails && (
      <div className="bg-white shadow p-6">
       <h2 className="text-xl font-bold mb-4">Payout Details</h2>
       <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto">
        {JSON.stringify(payout.payoutDetails, null, 2)}
       </pre>
      </div>
     )}

     {/* Metadata */}
     {payout.metadata && (
      <div className="bg-white shadow p-6">
       <h2 className="text-xl font-bold mb-4">Metadata</h2>
       <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto">
        {JSON.stringify(payout.metadata, null, 2)}
       </pre>
      </div>
     )}
    </div>
   </div>
  </div>
 );
};

export default PayoutDetail;

